import { put, call, delay, select } from 'redux-saga/effects';
import {
  changeModalState,
  errorsGlobalError,
  notify,
  setAppVersion,
  showNotifyFail,
  clearLoaders
} from '../../actions';
/* tslint:disable */
import { IDictionary, ISagaAction } from '../../types';
import { SagaIterator } from 'redux-saga';
import ApiErrors from '../../utils/API/APIErrors';
import Api from '../../utils/API';
import axios from '../../../helpers/axios';
import { getAppVersion, getAppVersionDelay } from '../../selectors';
import { getTranslationKey, ThrottleGuard } from '../../utils';

const toastThrottleGuard = new ThrottleGuard(2500);
const getToastKey = (status: string | undefined, title: string | undefined, message: string) =>
  `${status || ''}__${title || ''}__${message || ''}`;

export const notifySuccessSaga = function* ({ payload }: ISagaAction<{ message?: string }>) {
  yield put(
    notify({
      title: getTranslationKey('success'),
      message: payload?.message || getTranslationKey('saved'),
      status: 'success',
      position: 'top-center',
      dismissible: true,
      dismissAfter: 1500
    })
  );
};

export const notifyFailSaga = function* ({ payload }: ISagaAction<{ message?: string } | void>) {
  // @ts-ignore
  const title = getTranslationKey('error');
  // @ts-ignore
  const message = payload?.message || getTranslationKey('generalError');
  const status = 'error';

  const toastKey = getToastKey(status, title, message);
  if (!toastThrottleGuard.tryExecute(toastKey)) return;

  yield put(
    notify({
      title,
      message,
      status,
      position: 'top-center',
      dismissible: true,
      dismissAfter: 5000
    })
  );
};

export const showNotifyMessageSaga = function* ({ payload }: ISagaAction<string>): SagaIterator {
  yield put(
    notify({
      message: payload,
      status: 'warning',
      position: 'top-center',
      dismissible: true,
      dismissAfter: 10000
    })
  );
};

const maxTries = 3;
// const log = ( e: any ) => {
// 	window.logToServer( { message: e.message } );
// }
export const multipleAttempts = (generator: any, options: { skipRetryCodes?: number[] } = {}) =>
  function* multipleAttempts(...args: any) {
    let n = 0;
    let delayTime = 5;
    let response = {};
    const { skipRetryCodes = [] } = options;

    while (n < maxTries) {
      try {
        response = yield call(generator, ...args);
        ApiErrors.checkOnApiError(response);

        return response;
      } catch (e: unknown | any) {
        const isCancelled =
          e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || e?.name === 'AbortError';

        if (isCancelled) {
          return {
            error: e,
            cancelled: true
          };
        }

        // Bail out of retries when the failure is unrecoverable: either an
        // explicit caller-supplied skip code (e.g. S3 PUT 503) or a network
        // drop. Without the network-drop branch, a 5-wide rolling-window
        // upload that hits offline would emit 5 × maxTries toasts (each PUT
        // retried 3×) instead of 5. Caller sees `error` and skips queue
        // pushes; the end-of-saga retry in startUploadingProcessSaga
        // reconciles any image that never reached S3 PUT success.
        const nav = typeof navigator !== 'undefined' ? (navigator as any) : undefined;
        const isOffline = !(nav?.isOnline ?? nav?.onLine);
        const NETWORK_ERROR_CODES = ['ERR_NETWORK', 'ECONNABORTED'];
        const statusCode = e?.response?.status || e?.code;
        const isNetworkError =
          NETWORK_ERROR_CODES.includes(e?.code) ||
          e?.message === 'Network Error' ||
          (!e?.response && isOffline);

        if (isNetworkError || skipRetryCodes.includes(statusCode)) {
          return { error: e };
        }

        response = {
          error: e
        };
        console.error(e);
        /*
        if (window?.newrelic?.noticeError) {
          window.newrelic.noticeError('MultipleAttempts', e);
        }
        */
        // yield log( e );
      }
      if (n < maxTries - 1) {
        yield delay(delayTime * 1000);
        delayTime += 10;
      }

      n++;
    }

    return response;
  };

export const logUserActivitySaga = function* ({
  payload
}: ISagaAction<IDictionary<any>>): SagaIterator {
  try {
    const response = yield call(Api.Log.useractivitylog, payload);
    ApiErrors.checkOnApiError(response);
  } catch (e) {
    yield put(errorsGlobalError(e as ErrorEvent));
  }
};

export const getChecksumsSaga = function* (): SagaIterator {
  while (true) {
    const checkSumDelay = yield select(getAppVersionDelay);
    try {
      const appVersion = yield select(getAppVersion);
      const { data } = yield call(axios.get, `${window.location.origin}/checksums.json`);

      if (appVersion && appVersion !== data.app_version) {
        yield put(setAppVersion(data.app_version));
        yield put(
          changeModalState({
            key: 'appVersionModal',
            state: true
          })
        );
      } else {
        yield put(setAppVersion(data.app_version));
      }
    } catch (e) {
      console.log(e);
    }
    yield delay(checkSumDelay);
  }
};

/**
 * Reusable handler for offline/network error responses
 * @param response - API response object
 * @returns true if offline condition was handled, false otherwise
 */
export function* handleOfflineResponse(response: any, message?: string): SagaIterator<boolean> {
  if (response.code === 0) {
    yield put(
      showNotifyFail({
        message: message || getTranslationKey('offlineMessage')
      })
    );
    yield put(clearLoaders());

    return true;
  }

  return false;
}

/**
 * Non-generator version of handleOfflineResponse for use in regular async functions
 * @param response - API response object
 * @param dispatch - Redux dispatch function
 * @param message - Optional custom message
 * @returns true if offline condition was handled, false otherwise
 */
export const handleOfflineResponseSync = (
  response: any,
  dispatch: any,
  message?: string
): boolean => {
  if (response.code === 0) {
    dispatch(
      showNotifyFail({
        message: message || getTranslationKey('offlineMessage')
      })
    );
    dispatch(clearLoaders());

    return true;
  }

  return false;
};
